import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Copy, Lock, Loader2, ArrowRight, Shield, Send, AlertCircle, Mail, Plus, X, Timer, Zap, TrendingUp, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { trackInitiateCheckout, trackPurchase } from "../lib/tiktok";

const SITUATIONS = [
  "They replied dry",
  "They left me on read",
  "They ghosted and came back",
  "They cancelled plans",
  "We had a first date",
  "I want to ask for clarity",
  "I want to set a boundary",
  "I want to flirt / playfully reply",
  "I want to end the conversation calmly",
  "Other",
];
const GOALS = [
  "Keep the conversation going",
  "Sound calm and confident",
  "Make it playful",
  "Ask for clarity",
  "Set a boundary",
  "Avoid looking needy",
  "Show less availability",
  "End it with dignity",
  "I'm not sure — help me choose",
];
const TONES = ["Calm", "Warm", "Playful", "Short and confident", "Direct", "Soft but not needy", "Boundary-setting", "Polite but distant"];
const LANGS = ["English", "Arabic", "Egyptian Arabic", "Gulf Arabic", "Mixed Arabic/English"];

const RECHARGE_PACKAGES = [
  { key: "reply_rescue_5credits", title: "Rescue Session", price: "$9.99", desc: "5 reply credits", badge: "MOST POPULAR" },
  { key: "reply_rescue_15credits", title: "Deep Rescue", price: "$19.99", desc: "15 reply credits — best for ongoing conversations", badge: "BEST VALUE" },
  { key: "reply_rescue_24h_pass", title: "24h Reply Pass", price: "$14.99", desc: "Unlimited replies for 24 hours (fair use: 40)", badge: null },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-[12px] px-3 py-2 rounded-full border transition-colors min-h-[40px] ${
        active
          ? "bg-[#E8A18F] border-[#E8A18F] text-[#15081C] font-bold"
          : "bg-white/5 border-white/15 text-white/80 hover:border-white/30"
      }`}
    >
      {children}
    </button>
  );
}

const passTimeLeft = (expiresAt) => {
  const ms = new Date(expiresAt) - new Date();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function ReplySession() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const justPaid = params.get("payment") === "success";
  const paidCsId = params.get("cs");
  const paidPkg = params.get("pkg");

  // TikTok Purchase — fires exactly once per Stripe checkout session.
  // event_id `purchase_<cs_id>` matches the server-side webhook postback so
  // TikTok dedups browser + S2S into a single conversion.
  // Google Ads Purchase fires alongside via the same localStorage gate so a
  // refresh of the post-payment URL never double-counts either pixel.
  useEffect(() => {
    if (!justPaid || !paidCsId) return;
    const key = `rr_purchase_${paidCsId}`;
    try {
      if (localStorage.getItem(key)) return;
      trackPurchase(paidCsId, paidPkg || "reply_rescue_5credits");
      try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18239642780/eSJQCMzv7r4cEJy5q_lD",
            value: 1.0,
            currency: "EGP",
            transaction_id: paidCsId,
          });
        }
      } catch (_) { /* analytics must never break the flow */ }
      localStorage.setItem(key, "1");
    } catch (_) { /* private mode — server-side event still fires */ }
  }, [justPaid, paidCsId, paidPkg]);

  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargingPkg, setRechargingPkg] = useState(null);

  const [situation, setSituation] = useState("");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("Calm");
  const [language, setLanguage] = useState("English");
  const [conversation, setConversation] = useState("");
  const [context, setContext] = useState("");

  const [generating, setGenerating] = useState(false);
  const [reply, setReply] = useState(null);
  const [genErr, setGenErr] = useState("");

  const [email, setEmail] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // One-time "start here" tour for first-visit buyers (per session token).
  useEffect(() => {
    if (!session) return;
    try {
      const usable = (session.credits_remaining || 0) > 0 || (session.pass && session.pass.active);
      if (usable && !localStorage.getItem(`rr_tour_${token}`)) setShowTour(true);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!session]);

  const dismissTour = () => {
    if (!showTour) return;
    setShowTour(false);
    try { localStorage.setItem(`rr_tour_${token}`, "1"); } catch (_) {}
  };

  // Re-render every 30s so the pass countdown stays fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.title = "Reply Rescue — Private Session";
    if (!document.getElementById("rr-font")) {
      const l = document.createElement("link");
      l.id = "rr-font"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
      document.head.appendChild(l);
    }
    let icon = document.querySelector("link[rel='icon']");
    if (!icon) { icon = document.createElement("link"); icon.rel = "icon"; document.head.appendChild(icon); }
    icon.href = "/reply-rescue-icon.png";
  }, []);

  // Webhook may take a beat after the success redirect — poll 8× over ~12s.
  useEffect(() => {
    let cancel = false;
    let tries = 0;
    const fulfilled = (d) => (d.credits_remaining || 0) > 0 || (d.pass && d.pass.active);
    const run = async () => {
      try {
        const { data } = await api.get(`/reply/session/${token}`);
        if (!cancel) setSession(data);
        if (!cancel && justPaid && !fulfilled(data) && tries < 8) {
          tries += 1;
          setTimeout(run, 1500);
          return;
        }
      } catch (_) {
        if (!cancel && tries < 6) {
          tries += 1;
          setTimeout(run, 1500);
          return;
        }
      } finally {
        if (!cancel) setLoadingSession(false);
      }
    };
    run();
    return () => { cancel = true; };
  }, [token, justPaid]);

  const credits = session?.credits_remaining ?? 0;
  const pass = session?.pass || null;
  const passActive = !!(pass && pass.active && passTimeLeft(pass.expires_at) && pass.replies_used < pass.cap);
  const canUse = credits > 0 || passActive;
  const metrics = session?.metrics || { topups: 0, replies_generated: 0 };

  const canSubmit = useMemo(
    () => canUse && situation && goal && conversation.trim().length > 5 && !generating,
    [canUse, situation, goal, conversation, generating]
  );

  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Copy failed"); }
  };

  const copyPrivateLink = () => copy(window.location.origin + `/reply/session/${token}`, "Private link copied");

  const generate = async () => {
    if (!canSubmit) return;
    setGenerating(true); setGenErr(""); setReply(null);
    try {
      const { data } = await api.post("/reply/generate", {
        public_token: token, situation, goal, tone, language, conversation, context,
      });
      const { reply: r, ...sess } = data;
      setReply(r);
      setSession(sess);
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI temporarily unavailable. No credit was used.";
      setGenErr(msg);
      // Refresh state in case the server refunded.
      try { const { data } = await api.get(`/reply/session/${token}`); setSession(data); } catch (_) {}
    } finally {
      setGenerating(false);
    }
  };

  const recharge = async (pkg) => {
    setRechargingPkg(pkg);
    trackInitiateCheckout(pkg);
    // Google Ads "Add to cart" — same dedup flag as LandingReply so a buyer
    // who recharges within the same session doesn't double-count.
    try {
      if (typeof window !== "undefined" && typeof window.gtag === "function" && !window.__rr_atc_fired) {
        window.__rr_atc_fired = true;
        window.gtag("event", "conversion", {
          send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
          value: 1.0,
          currency: "EGP",
        });
      }
    } catch (_) { /* analytics must never break the funnel */ }
    try {
      const { data } = await api.post("/reply/checkout", { public_token: token, package: pkg });
      window.location.href = data.checkout_url;
    } catch (_) {
      toast.error("Could not start recharge.");
      setRechargingPkg(null);
    }
  };

  const saveEmail = async () => {
    const v = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { toast.error("Enter a valid email"); return; }
    setSavingEmail(true);
    try {
      const { data } = await api.post("/reply/save-link", { public_token: token, email: v });
      setSession((s) => ({ ...(s || {}), email_saved: true }));
      toast.success(data.email_sent ? "Link sent — check your inbox" : "Email saved");
    } catch (_) {
      toast.error("Could not save your email. Try again.");
    } finally {
      setSavingEmail(false);
    }
  };

  if (loadingSession) {
    return (
      <div className="min-h-screen bg-[#15081C] text-white flex items-center justify-center" dir="ltr">
        <Loader2 className="w-6 h-6 animate-spin text-[#E8A18F]" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#15081C] text-white flex flex-col items-center justify-center px-5 text-center" dir="ltr">
        <AlertCircle className="w-10 h-10 text-[#F0838F] mb-4" />
        <h1 className="font-black text-xl">Session not found</h1>
        <p className="text-white/60 text-sm mt-2 max-w-md">This private link is invalid or expired. Start a new Reply Rescue session below.</p>
        <a href="/reply" className="mt-5 bg-gradient-to-r from-[#EFA98F] to-[#D9707F] text-[#2B1230] font-extrabold px-5 py-3 rounded-full">Open Reply Rescue</a>
      </div>
    );
  }

  // Paywall copy variations
  let paywallTitle = "Need more replies?";
  let paywallSub = "Keep the conversation going — pick a recharge option.";
  if (!canUse && pass && new Date(pass.expires_at) <= new Date()) {
    paywallTitle = "Your 24h pass has expired";
    paywallSub = "Recharge with credits or grab another 24h pass to continue.";
  } else if (!canUse && pass && pass.replies_used >= pass.cap) {
    paywallTitle = "Pass limit reached (40 replies)";
    paywallSub = "You hit the fair-use limit. Recharge to keep going.";
  }

  return (
    <div
      dir="ltr"
      lang="en"
      className="min-h-screen bg-[#15081C] text-[#F6EFE7]"
      style={{ fontFamily: "'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif" }}
      data-testid="reply-session-root"
    >
      {/* HEADER */}
      <header className="sticky top-0 z-30 bg-[#15081C]/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <img src="/reply-rescue-logo.png" alt="Reply Rescue" className="h-7 w-auto shrink-0" data-testid="session-logo" />
          <div className="leading-tight hidden sm:block">
            <div className="font-black text-[14px]">Private Session</div>
            <div className="text-[10px] text-white/55 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Tokenized access</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {passActive && (
              <div className="bg-[#C9B8E8]/10 border border-[#C9B8E8]/40 rounded-md px-2.5 py-1.5" data-testid="pass-pill">
                <div className="text-[9px] text-white/55 tracking-wider flex items-center gap-1"><Timer className="w-2.5 h-2.5" /> 24H PASS</div>
                <div className="font-black text-[#C9B8E8] text-[13px] leading-none" data-testid="pass-time-left">
                  {passTimeLeft(pass.expires_at)} · {pass.replies_used}/{pass.cap}
                </div>
              </div>
            )}
            <div className="bg-[#E8A18F]/10 border border-[#E8A18F]/40 rounded-md px-3 py-1.5" data-testid="credits-pill">
              <div className="text-[9px] text-white/55 tracking-wider">CREDITS</div>
              <div className="font-black text-[#E8A18F] text-base leading-none" data-testid="credits-count">{credits}</div>
            </div>
            <button
              onClick={() => setRechargeOpen(true)}
              data-testid="open-recharge-btn"
              className="bg-white/10 hover:bg-white/15 border border-white/15 rounded-md p-2 min-h-[40px] min-w-[40px] flex items-center justify-center"
              title="Get more replies"
            >
              <Plus className="w-4 h-4 text-[#E8A18F]" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-5 py-6 sm:py-8 space-y-6">
        {/* METRICS STRIP */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[12px] text-white/55" data-testid="session-metrics">
          <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#E8A18F]" /> Top-ups: <span className="font-bold text-white" data-testid="metric-topups">{metrics.topups}</span></span>
          <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[#C9B8E8]" /> Replies generated: <span className="font-bold text-white" data-testid="metric-replies">{metrics.replies_generated}</span></span>
          {passActive && <span className="flex items-center gap-1.5"><Timer className="w-3.5 h-3.5 text-[#C9B8E8]" /> Pass replies used: <span className="font-bold text-white">{pass.replies_used}/{pass.cap}</span></span>}
        </div>

        {/* SAVE THIS PAGE */}
        <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-testid="save-page-box">
          <Shield className="w-5 h-5 text-[#C9B8E8] shrink-0" />
          <div className="text-[13px] text-white/75 leading-snug flex-1">
            <span className="font-bold text-white">Save this page.</span> This is your private access link.
            If you close the browser, use the link to return to your remaining replies.
          </div>
          <button onClick={copyPrivateLink} data-testid="copy-private-link" className="text-[12px] bg-white/10 hover:bg-white/15 px-3 py-2 rounded-md font-semibold flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Copy link
          </button>
        </div>

        {/* EMAIL CAPTURE */}
        {!session.email_saved && (
          <div className="bg-[#C9B8E8]/[0.05] border border-[#C9B8E8]/25 rounded-lg p-4" data-testid="email-capture-box">
            <div className="flex items-center gap-2 text-[13px] font-bold">
              <Mail className="w-4 h-4 text-[#C9B8E8]" /> Never lose this link — email it to yourself
            </div>
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-white/[0.05] border border-white/15 focus:border-[#C9B8E8]/60 rounded-md px-3 py-2.5 text-[14px] outline-none"
                data-testid="email-capture-input"
              />
              <button
                onClick={saveEmail}
                disabled={savingEmail}
                data-testid="email-capture-submit"
                className="bg-[#C9B8E8] hover:bg-[#B9A5E2] text-[#15081C] font-black text-[13px] px-5 py-2.5 rounded-md disabled:opacity-50 min-h-[42px] flex items-center justify-center gap-1.5"
              >
                {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Email me my link</>}
              </button>
            </div>
            <div className="mt-2 text-[11px] text-white/40">We only use this to send your private link. No spam.</div>
          </div>
        )}

        {/* PAYWALL */}
        {!canUse && (
          <div className="bg-gradient-to-br from-[#E8A18F]/10 to-transparent border border-[#E8A18F]/40 rounded-xl p-6 text-center" data-testid="recharge-paywall">
            <h2 className="font-black text-lg sm:text-xl" data-testid="paywall-title">{paywallTitle}</h2>
            <p className="text-[13px] text-white/65 mt-2">{paywallSub}</p>
            <button
              onClick={() => setRechargeOpen(true)}
              data-testid="recharge-cta"
              className="mt-5 bg-gradient-to-r from-[#EFA98F] to-[#D9707F] hover:from-[#f2b39a] hover:to-[#e07e8c] text-[#2B1230] font-extrabold px-6 py-3.5 rounded-full inline-flex items-center gap-2 min-h-[48px] shadow-[0_8px_28px_-8px_rgba(217,112,127,0.6)]"
            >
              See recharge options <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* CHAT FORM */}
        <div className={`space-y-6 ${!canUse ? "opacity-50 pointer-events-none" : ""}`}>
          {/* Situation */}
          <section className="relative" data-testid="tour-step1">
            {showTour && (
              <>
                <div className="absolute -inset-3 rounded-2xl border-2 border-[#D9707F]/70 animate-pulse pointer-events-none" aria-hidden />
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#EFA98F] to-[#D9707F] text-[#2B1230] text-[10px] font-extrabold tracking-wider px-3.5 py-1.5 rounded-full shadow-[0_6px_20px_-6px_rgba(217,112,127,0.7)] z-10 whitespace-nowrap flex items-center gap-1.5"
                  data-testid="tour-start-here"
                >
                  <Sparkles className="w-3 h-3" /> START HERE — PICK WHAT HAPPENED
                </div>
              </>
            )}
            <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">1 · WHAT HAPPENED?</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SITUATIONS.map((s) => (
                <Chip key={s} active={situation === s} onClick={() => { setSituation(s); dismissTour(); }} testid={`sit-${s.slice(0,10)}`}>{s}</Chip>
              ))}
            </div>
          </section>

          {/* Goal */}
          <section>
            <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">2 · WHAT DO YOU WANT THE REPLY TO ACHIEVE?</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {GOALS.map((g) => (
                <Chip key={g} active={goal === g} onClick={() => setGoal(g)} testid={`goal-${g.slice(0,10)}`}>{g}</Chip>
              ))}
            </div>
          </section>

          {/* Tone + Language */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">3 · TONE</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {TONES.map((t) => (<Chip key={t} active={tone === t} onClick={() => setTone(t)}>{t}</Chip>))}
              </div>
            </div>
            <div>
              <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">4 · LANGUAGE</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {LANGS.map((l) => (<Chip key={l} active={language === l} onClick={() => setLanguage(l)}>{l}</Chip>))}
              </div>
            </div>
          </section>

          {/* Conversation paste */}
          <section>
            <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">5 · PASTE THE LATEST PART OF THE CHAT</label>
            <textarea
              value={conversation}
              onChange={(e) => setConversation(e.target.value)}
              placeholder="Paste only the part needed to understand the situation. Remove names, phone numbers, addresses, or private details."
              rows={6}
              className="mt-2 w-full bg-white/[0.04] border border-white/15 focus:border-[#E8A18F]/60 rounded-lg p-3.5 text-[14px] outline-none resize-y"
              data-testid="paste-conversation"
            />
          </section>

          {/* Optional context */}
          <section>
            <label className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold">6 · ANYTHING IMPORTANT WE SHOULD KNOW? <span className="text-white/40">(optional)</span></label>
            <input
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Example: We went out once, they cancelled twice, I don't want to seem angry…"
              className="mt-2 w-full bg-white/[0.04] border border-white/15 focus:border-[#E8A18F]/60 rounded-lg p-3 text-[14px] outline-none"
              data-testid="paste-context"
            />
          </section>

          {/* Submit */}
          <div className="pt-2">
            <button
              onClick={generate}
              disabled={!canSubmit}
              data-testid="generate-btn"
              className="w-full sm:w-auto bg-gradient-to-r from-[#EFA98F] to-[#D9707F] hover:from-[#f2b39a] hover:to-[#e07e8c] disabled:opacity-40 disabled:cursor-not-allowed text-[#2B1230] font-extrabold text-base px-7 py-4 rounded-full transition-all flex items-center justify-center gap-2 min-h-[52px] shadow-[0_8px_28px_-8px_rgba(217,112,127,0.6)]"
            >
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Send className="w-4 h-4" /> {passActive ? "Generate My Reply (pass)" : "Generate My Reply (1 credit)"}</>}
            </button>
            <div className="mt-2 text-[11px] text-white/45">
              {passActive ? "Your 24h pass covers this reply — no credit used." : "1 credit will be used only after a successful reply."}
            </div>
          </div>

          {genErr && (
            <div className="bg-[#F0838F]/10 border border-[#F0838F]/30 rounded-lg p-4 text-[13px] text-[#F4B3B9]" data-testid="gen-err">
              {genErr}
            </div>
          )}
        </div>

        {/* REPLY OUTPUT */}
        {reply && (
          <section className="space-y-4 mt-2" data-testid="ai-reply-output">
            <ReplyCard label="SEND THIS" text={reply.send_this} onCopy={() => copy(reply.send_this, "Reply copied")} primary />
            {reply.alternatives?.softer && <ReplyCard label="SOFTER ALTERNATIVE" text={reply.alternatives.softer} onCopy={() => copy(reply.alternatives.softer, "Copied")} />}
            {reply.alternatives?.more_confident && <ReplyCard label="MORE CONFIDENT ALTERNATIVE" text={reply.alternatives.more_confident} onCopy={() => copy(reply.alternatives.more_confident, "Copied")} />}
            {Array.isArray(reply.why_this_works) && reply.why_this_works.length > 0 && (
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                <div className="text-[11px] tracking-[0.2em] text-[#C9B8E8] font-bold mb-2">WHY THIS WORKS</div>
                <ul className="space-y-1.5 text-[13px] text-white/80">
                  {reply.why_this_works.map((b, i) => (<li key={i}>· {b}</li>))}
                </ul>
              </div>
            )}
            {reply.do_not_send?.example && (
              <div className="bg-[#F0838F]/5 border border-[#F0838F]/30 rounded-xl p-4">
                <div className="text-[11px] tracking-[0.2em] text-[#F0838F] font-bold mb-2">DO NOT SEND</div>
                <div className="text-[13px] text-white/75 italic line-through">&quot;{reply.do_not_send.example}&quot;</div>
                <div className="text-[12px] text-white/55 mt-2">{reply.do_not_send.why}</div>
              </div>
            )}
            {reply.next_move && (
              <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 space-y-2 text-[13px]">
                <div className="text-[11px] tracking-[0.2em] text-[#E8A18F] font-bold mb-1">NEXT MOVE</div>
                {reply.next_move.if_positive_reply && <div><span className="font-bold text-[#C9B8E8]">If they reply positively:</span> <span className="text-white/75">{reply.next_move.if_positive_reply}</span></div>}
                {reply.next_move.if_cold_reply && <div><span className="font-bold text-[#E8C18F]">If they reply coldly:</span> <span className="text-white/75">{reply.next_move.if_cold_reply}</span></div>}
                {reply.next_move.if_no_reply && <div><span className="font-bold text-white/60">If they don&apos;t reply:</span> <span className="text-white/75">{reply.next_move.if_no_reply}</span></div>}
              </div>
            )}
            <div className="text-[12px] text-white/45 text-center">
              {passActive
                ? <>Pass replies used: <span className="font-bold text-white">{pass.replies_used}/{pass.cap}</span></>
                : <>Credits remaining: <span className="font-bold text-white">{credits}</span></>}
            </div>
          </section>
        )}

        <footer className="pt-8 pb-12 text-[11px] text-white/35 text-center leading-relaxed">
          Reply Rescue is an AI-powered communication assistant. It does not guarantee outcomes,
          replies, dates, relationships, or behavior from other people.
        </footer>
      </div>

      {/* RECHARGE MODAL */}
      {rechargeOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="recharge-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setRechargeOpen(false)} />
          <div className="relative w-full sm:max-w-md bg-[#221130] border border-white/10 rounded-t-2xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-black text-lg">Get more replies</h2>
              <button onClick={() => setRechargeOpen(false)} data-testid="recharge-modal-close" className="p-2 rounded-md hover:bg-white/10">
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>
            <p className="text-[12px] text-white/55 mb-5">One-time payment · added instantly to this private session.</p>
            <div className="space-y-3">
              {RECHARGE_PACKAGES.map((p) => (
                <button
                  key={p.key}
                  onClick={() => recharge(p.key)}
                  disabled={!!rechargingPkg}
                  data-testid={`recharge-pkg-${p.key}`}
                  className={`w-full text-left rounded-xl border p-4 transition-colors disabled:opacity-50 ${
                    p.badge === "BEST VALUE"
                      ? "border-[#E8A18F]/60 bg-[#E8A18F]/[0.07] hover:bg-[#E8A18F]/[0.12]"
                      : "border-white/12 bg-white/[0.04] hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-black text-[15px] flex items-center gap-2">
                        {p.title}
                        {p.badge && <span className="text-[9px] bg-[#E8A18F] text-[#15081C] font-black px-2 py-0.5 rounded-full tracking-wider">{p.badge}</span>}
                      </div>
                      <div className="text-[12px] text-white/55 mt-1">{p.desc}</div>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <div className="font-black text-[#E8A18F] text-lg">{p.price}</div>
                      {rechargingPkg === p.key
                        ? <Loader2 className="w-4 h-4 animate-spin text-white/60 ml-auto mt-1" />
                        : <ArrowRight className="w-4 h-4 text-white/40 ml-auto mt-1" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-[11px] text-white/40 flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3 text-[#C9B8E8]" /> Secure Stripe checkout · No subscription
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReplyCard({ label, text, onCopy, primary = false }) {
  return (
    <div className={`rounded-xl p-4 ${primary ? "bg-gradient-to-br from-[#E8A18F]/15 to-transparent border border-[#E8A18F]/50" : "bg-white/[0.04] border border-white/10"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className={`text-[11px] tracking-[0.2em] font-bold ${primary ? "text-[#E8A18F]" : "text-white/55"}`}>{label}</div>
        <button onClick={onCopy} className="text-[11px] bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded font-semibold flex items-center gap-1.5" data-testid={`copy-${label.toLowerCase().replace(/\s/g,"-")}`}>
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <div className="text-[14px] sm:text-[15px] text-white leading-relaxed whitespace-pre-wrap">{text}</div>
    </div>
  );
}
